/**
 * Check dates without a year (owner 2026-10-24), as shoppers read them: "yesterday", "3 days ago", "2 weeks ago", then
 * "Oct 3" (this year) or "Oct 3, 2025" (another year). Used by the "Didn't work when we last checked · {date}" label
 * (SEO R3 rule 6) through components/CheckDate. Pure; everything is UTC so server and client agree.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function toDate(value: Date | string | null | undefined): Date | null {
    if (value === null || value === undefined || value === "") return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-10-10" (UTC) for <time dateTime>. */
export function isoDayOf(value: Date | string | null | undefined): string | null {
    const d = toDate(value);
    return d ? d.toISOString().slice(0, 10) : null;
}

/** "Oct 8": the clock-independent server fallback. */
export function formatMonthDay(value: Date | string | null | undefined): string | null {
    const d = toDate(value);
    return d ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}` : null;
}

/** "Oct 8, 2026": the tooltip. */
export function formatFullDate(value: Date | string | null | undefined): string | null {
    const d = toDate(value);
    return d ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}` : null;
}

/** "Oct 8" in the current year, "Oct 8, 2025" for another year. Clock dependent: client side only. */
export function formatMonthDayOrYear(value: Date | string | null | undefined, now: Date = new Date()): string | null {
    const d = toDate(value);
    if (!d) return null;
    return d.getUTCFullYear() === now.getUTCFullYear() ? formatMonthDay(d) : formatFullDate(d);
}

/**
 * Relative up to 30 days ("today", "yesterday", "3 days ago", "2 weeks ago"), then month and day (with the year only
 * for another year). `relative` tells the caller whether "on" reads right before it. Clock dependent: after mount only.
 */
export function checkDateText(value: Date | string | null | undefined, now: Date = new Date()): { text: string; relative: boolean } | null {
    const d = toDate(value);
    if (!d) return null;
    const startOfToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const startOfDay = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const days = Math.round((startOfToday - startOfDay) / 86_400_000);
    if (days >= 0 && days < 30) {
        if (days === 0) return { text: "today", relative: true };
        if (days === 1) return { text: "yesterday", relative: true };
        if (days < 7) return { text: `${days} days ago`, relative: true };
        const weeks = Math.floor(days / 7);
        return { text: `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`, relative: true };
    }
    return { text: formatMonthDayOrYear(d, now)!, relative: false };
}
